import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useProducts } from "../context/ProductContext";
import { createProduct } from "../services/api";
import { CreateProductPayload } from "../types/product";

const Container = styled.div`
  max-width: 640px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text};
  letter-spacing: -0.02em;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 2rem;
`;

const Form = styled.form`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.xl};
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

const Required = styled.span`
  color: ${({ theme }) => theme.colors.error};
  margin-left: 2px;
`;

const Input = styled.input<{ $hasError?: boolean }>`
  padding: 0.7rem 0.9rem;
  border: 1px solid
    ${({ $hasError, theme }) =>
      $hasError ? theme.colors.error : theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.md};
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.surface};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${({ $hasError, theme }) =>
      $hasError ? theme.colors.error : theme.colors.primary};
    box-shadow: 0 0 0 3px
      ${({ $hasError, theme }) =>
        $hasError ? theme.colors.errorLight : theme.colors.primaryLight};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textLight};
  }
`;

const TextArea = styled.textarea<{ $hasError?: boolean }>`
  padding: 0.7rem 0.9rem;
  border: 1px solid
    ${({ $hasError, theme }) =>
      $hasError ? theme.colors.error : theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.md};
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.surface};
  resize: vertical;
  min-height: 120px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${({ $hasError, theme }) =>
      $hasError ? theme.colors.error : theme.colors.primary};
    box-shadow: 0 0 0 3px
      ${({ $hasError, theme }) =>
        $hasError ? theme.colors.errorLight : theme.colors.primaryLight};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textLight};
  }
`;

const Select = styled.select<{ $hasError?: boolean }>`
  padding: 0.7rem 0.9rem;
  border: 1px solid
    ${({ $hasError, theme }) =>
      $hasError ? theme.colors.error : theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.md};
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.surface};
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${({ $hasError, theme }) =>
      $hasError ? theme.colors.error : theme.colors.primary};
    box-shadow: 0 0 0 3px
      ${({ $hasError, theme }) =>
        $hasError ? theme.colors.errorLight : theme.colors.primaryLight};
  }
`;

const FieldError = styled.span`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.error};
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 0.5rem;
`;

const SubmitButton = styled.button<{ disabled?: boolean }>`
  padding: 0.75rem 2rem;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border-radius: ${({ theme }) => theme.radii.md};
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s;
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primaryHover};
  }
`;

const CancelButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.textSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.md};
  font-weight: 500;
  font-size: 0.9rem;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.background};
    border-color: ${({ theme }) => theme.colors.borderHover};
  }
`;

const SuccessBanner = styled.div`
  padding: 1rem 1.25rem;
  background: ${({ theme }) => theme.colors.successLight};
  color: ${({ theme }) => theme.colors.successText};
  border-radius: ${({ theme }) => theme.radii.md};
  font-weight: 500;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ErrorBanner = styled.div`
  padding: 1rem 1.25rem;
  background: ${({ theme }) => theme.colors.errorLight};
  color: ${({ theme }) => theme.colors.errorText};
  border-radius: ${({ theme }) => theme.radii.md};
  font-weight: 500;
  font-size: 0.9rem;
`;

interface FormErrors {
  title?: string;
  description?: string;
  price?: string;
  category?: string;
  image?: string;
}

const CreateProductPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    state: { categories },
    loadCategories,
  } = useProducts();

  const [formData, setFormData] = useState<CreateProductPayload>({
    title: "",
    description: "",
    price: 0,
    category: "",
    image: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    if (categories.length === 0) {
      loadCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    }

    if (!formData.price || formData.price <= 0) {
      newErrors.price = "Price must be a positive number";
    }

    if (!formData.category) {
      newErrors.category = "Please select a category";
    }

    if (!formData.image.trim()) {
      newErrors.image = "Image URL is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "price" ? parseFloat(value) || 0 : value,
    }));

    // Clear field error on change
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitSuccess(false);

    if (!validate()) return;

    setSubmitting(true);
    try {
      await createProduct(formData);
      setSubmitSuccess(true);
      setFormData({
        title: "",
        description: "",
        price: 0,
        category: "",
        image: "",
      });
    } catch (err) {
      setSubmitError("Failed to create product. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container>
      <PageTitle>Add New Product</PageTitle>
      <Subtitle>
        Fill in the details below to create a new product listing.
      </Subtitle>

      <Form onSubmit={handleSubmit}>
        {submitSuccess && (
          <SuccessBanner>✅ Product created successfully!</SuccessBanner>
        )}

        {submitError && <ErrorBanner>{submitError}</ErrorBanner>}

        <FormGroup>
          <Label>
            Title <Required>*</Required>
          </Label>
          <Input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder="Enter product title"
            $hasError={!!errors.title}
          />
          {errors.title && <FieldError>{errors.title}</FieldError>}
        </FormGroup>

        <FormGroup>
          <Label>
            Description <Required>*</Required>
          </Label>
          <TextArea
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Enter product description"
            $hasError={!!errors.description}
          />
          {errors.description && <FieldError>{errors.description}</FieldError>}
        </FormGroup>

        <FormGroup>
          <Label>
            Price ($) <Required>*</Required>
          </Label>
          <Input
            type="number"
            name="price"
            value={formData.price || ""}
            onChange={handleChange}
            placeholder="0.00"
            min="0"
            step="0.01"
            $hasError={!!errors.price}
          />
          {errors.price && <FieldError>{errors.price}</FieldError>}
        </FormGroup>

        <FormGroup>
          <Label>
            Category <Required>*</Required>
          </Label>
          <Select
            name="category"
            value={formData.category}
            onChange={handleChange}
            $hasError={!!errors.category}
          >
            <option value="">Select a category</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat.charAt(0).toUpperCase() + cat.slice(1)}
              </option>
            ))}
          </Select>
          {errors.category && <FieldError>{errors.category}</FieldError>}
        </FormGroup>

        <FormGroup>
          <Label>
            Image URL <Required>*</Required>
          </Label>
          <Input
            type="text"
            name="image"
            value={formData.image}
            onChange={handleChange}
            placeholder="https://example.com/image.jpg"
            $hasError={!!errors.image}
          />
          {errors.image && <FieldError>{errors.image}</FieldError>}
        </FormGroup>

        <ButtonRow>
          <CancelButton type="button" onClick={() => navigate("/")}>
            Cancel
          </CancelButton>
          <SubmitButton type="submit" disabled={submitting}>
            {submitting ? "Creating..." : "Create Product"}
          </SubmitButton>
        </ButtonRow>
      </Form>
    </Container>
  );
};

export default CreateProductPage;
